import * as React from "react";

import { AnalyticsPageHeader } from "@editor/components/analytics/AnalyticsPageHeader";
import { ConditionallyBlurredComponent } from "@editor/components/analytics/ConditionallyBlurredComponent";
import { Loader } from "@editor/components/common/Loader";
import { ConnectShopifyCallout } from "@editor/components/editor/page/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import {
  useCurrentWorkspaceContext,
  useCurrentWorkspaceId,
} from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useOpenModal } from "@editor/hooks/useModal";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { routes } from "@editor/utils/router";

import AnalyticsContext from "@/features/analytics/AnalyticsContext";
import { useAnalyticsOnboardingOAuthLink } from "@/features/analytics/useAnalyticsOnboaredingOAuthLink";
import useAnalyticsStatus from "@/features/analytics/useAnalyticsStatus";
import AnnouncementMessage from "@/features/announcements/AnnouncementMessage";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import PageHeader from "@replo/design-system/components/PageHeader";
import { Card } from "@replo/design-system/components/shadcn/core/card";
import { Tabs } from "@replo/design-system/components/tabs/Tabs";
import { Megaphone, Rocket } from "lucide-react";
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const ShopifyReconnectBanner: React.FC = () => {
  const { oauthLink, isLoading: isLoadingOauthLink } =
    useAnalyticsOnboardingOAuthLink();

  return (
    <InlineAlert
      variant="announcement"
      startEnhancer={<Megaphone size={16} className="text-beta" />}
      endEnhancer={
        <Button
          variant="tertiary"
          size="sm"
          to={oauthLink ?? ""}
          isLoading={isLoadingOauthLink || !oauthLink}
        >
          Reconnect Store
        </Button>
      }
    >
      Seeing inaccurate or missing data? You may need to reconnect your Shopify
      Store.
    </InlineAlert>
  );
};

const ANALYTICS_TABS: {
  value: string;
  label: string;
}[] = [
  ...(isFeatureEnabled("analytics-v2-data-model")
    ? [{ value: "home", label: "Overview" }]
    : []),
  { value: "insights", label: "Insights" },
  { value: "overview", label: "Pages" },
];

export const ProjectAnalyticsDashboard: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId() ?? null;
  const logEvent = useLogAnalytics();

  const projectName = project?.name;

  const handleTabChange = (value: string) => {
    const route = exhaustiveSwitch({ type: value })({
      home: () => routes.shops.analytics.home,
      overview: () => routes.shops.analytics.overview,
      insights: () => routes.shops.analytics.insights,
    });
    const path = generatePath(route, {
      projectId: project?.id ?? "",
    });
    navigate(path);

    // Map the actual tab value to analytics event tab names
    const analyticsTabName = exhaustiveSwitch({ type: value })({
      home: () => "analytics_home" as const,
      overview: () => "analytics_overview" as const,
      insights: () => "analytics_insights" as const,
    });

    logEvent("dashboard.nav.click", {
      tab: analyticsTabName,
      userId: user?.id ?? null,
      workspaceId,
    });
  };

  return (
    <AnalyticsDashboard
      onTabChange={handleTabChange}
      pageHeaderTitle={projectName ? `${projectName} Analytics` : "Analytics"}
    />
  );
};

export const WorkspaceAnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();

  const { workspace } = useCurrentWorkspaceContext();

  const workspaceName = workspace?.name;

  const handleTabChange = (value: string) => {
    const route = exhaustiveSwitch({ type: value })({
      home: () => routes.analytics.home,
      overview: () => routes.analytics.overview,
      insights: () => routes.analytics.insights,
    });
    const path = generatePath(route, {
      workspaceId: workspace?.id ?? "",
    });
    navigate(path);
  };

  return (
    <AnalyticsDashboard
      onTabChange={handleTabChange}
      pageHeaderTitle={
        workspaceName ? `${workspaceName} Analytics` : "Analytics"
      }
    />
  );
};

type AnalyticsDashboardProps = {
  pageHeaderTitle: string;
  onTabChange: (value: string) => void;
};
const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  pageHeaderTitle,
  onTabChange,
}) => {
  const { shouldBlurFullScreen, isLoading, dataDisplayType } =
    useAnalyticsStatus();
  const pathName = useLocation().pathname.split("/").pop();
  const isDeepDive = pathName === "deep-dive";

  const openModal = useOpenModal();

  const handleOpenBillingModal = () =>
    openModal({
      type: "billingModal",
      props: {
        source: "analytics",
        billingPlanMessageKey: "billingPlan.analytics",
      },
    });

  if (isLoading) {
    return <Loader />;
  }

  const teaserComponent = exhaustiveSwitch({ type: dataDisplayType })({
    blurFullScreenRequireShopifyConnect: () => <ConnectShopifyTeaser />,
    blurFullScreenRequireUpgrade: () =>
      pathName && (
        <FakeDataTeaser
          path={pathName}
          onOpenBillingModal={handleOpenBillingModal}
        />
      ),
  });

  return (
    <AnalyticsContext>
      <div className="w-full px-6 flex flex-col overflow-hidden h-full">
        {!isDeepDive && (
          <AnalyticsPageHeader
            header={
              <PageHeader title={pageHeaderTitle} className="border-none" />
            }
          />
        )}
        <div className="flex flex-col gap-4 overflow-hidden flex-1 h-full">
          <AnnouncementMessage tags={["Insights"]} />
          {isFeatureEnabled("shopify-webpixel-reauth-banner") && (
            <ShopifyReconnectBanner />
          )}
          <div>
            {pathName !== "deep-dive" && (
              <Tabs
                tabsOptions={ANALYTICS_TABS}
                value={pathName ?? "home"}
                onValueChange={onTabChange}
                size="base"
                defaultValue="home"
              />
            )}
          </div>
          <ConditionallyBlurredComponent
            shouldBlur={shouldBlurFullScreen}
            componentToBlur={
              dataDisplayType === "partialData" && pathName ? (
                <OutletWithPartialDataBanner
                  path={pathName}
                  onOpenBillingModal={handleOpenBillingModal}
                />
              ) : (
                <Outlet />
              )
            }
            visibleComponent={teaserComponent}
            visibleComponentClassName="mt-25"
          />
        </div>
      </div>
    </AnalyticsContext>
  );
};

const OutletWithPartialDataBanner: React.FC<{
  onOpenBillingModal: () => void;
  path: string;
}> = ({ onOpenBillingModal, path }) => {
  const { title, description } = exhaustiveSwitch({ type: path })({
    home: () => ({
      title: "See the Full Picture",
      description:
        "Upgrade to start tracking all data across all your store’s pages, products, and users.",
    }),
    insights: () => ({
      title: "We Found More Insights",
      description: "Upgrade to access all growth opportunities.",
    }),
    overview: () => ({
      title: "See How All Your Pages Are Performing",
      description:
        "Unlock full analytics to view all pages and advanced filters.",
    }),
    "deep-dive": () => ({
      title: "See the Full Picture",
      description: "See all Page analytics with a paid plan.",
    }),
  });

  return (
    <div className="relative h-full overflow-hidden">
      <div className="h-full overflow-auto">
        <Outlet />
      </div>
      <div className="absolute bottom-0 left-0 z-10 w-full">
        <div className="bg-default rounded-lg p-4 flex flex-row justify-between items-center">
          <div className="flex flex-row gap-3">
            <div className="bg-info-soft rounded-full p-2 flex items-center justify-center h-[48px] w-[48px]">
              <Rocket size={18} className="text-graph-primary-h" />
            </div>
            <div className="flex flex-col text-white">
              <span className="header-h4">{title}</span>
              <span className="text-base">{description}</span>
            </div>
          </div>
          <Button
            variant="secondary"
            size="base"
            startEnhancer={<Rocket size={14} />}
            onClick={onOpenBillingModal}
          >
            Unlock
          </Button>
        </div>
      </div>
    </div>
  );
};

const ConnectShopifyTeaser: React.FC = () => {
  const { project } = useCurrentProjectContext();
  if (!project) {
    return null;
  }

  return (
    <Card className="bg-white w-[350px]">
      <ConnectShopifyCallout
        cookieValue={{ type: "analytics", projectId: project.id }}
      />
    </Card>
  );
};

const FakeDataTeaser: React.FC<{
  path: string;
  onOpenBillingModal: () => void;
}> = ({ path, onOpenBillingModal }) => {
  const { image, title, description } = exhaustiveSwitch({ type: path })({
    home: () => ({
      image: "/images/analytics/fake-data/fake-homepage.svg",
      title: "Measure Store Performance",
      description:
        "Upgrade to start tracking your store’s pages, products, and users.",
    }),
    insights: () => ({
      image: "/images/analytics/fake-data/fake-insights.svg",
      title: "Find Opportunities to Act on",
      description:
        "Even while you sleep, Insights is working to spot problems and unlock wins.",
    }),
    overview: () => ({
      image: "/images/analytics/fake-data/fake-pages.svg",
      title: "Know Whats Happens on Every Page",
      description:
        "See which pages drive sales, where you lose visitors, and how to grow faster.",
    }),
  });

  return (
    <Card className="bg-white">
      <div className="space-y-4 items-center flex flex-col">
        <img src={image} alt="Analytics Preview Image" />
        <div className="flex flex-col gap-1 items-center w-[530px]">
          <span className="header-h2">{title}</span>
          <span className="text-base text-muted text-center">
            {description}
          </span>
        </div>
        <Button
          size="base"
          variant="primary"
          isLoading={false}
          onClick={onOpenBillingModal}
        >
          Unlock Analytics
        </Button>
      </div>
    </Card>
  );
};
